// Google Drive Sync card on the project settings tab: root folders plus individually
// tracked files from one connected shared drive, synced through one authorizer's Google
// account. With no config yet, the viewer connects Google and chooses the shared drive.
// Once connected, only the authorizer can reconnect; anyone on the project can add from
// the shared drive, remove, edit exclusions, sync now or disconnect.
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  DriveFolderNode,
  DriveSyncRoot,
  DriveSyncState,
  DriveTrackedFile,
  useAddDriveSources,
  useDisconnectDriveSync,
  useUseMyAccountForDriveSync,
  useDriveFolderTree,
  useDriveSyncOperation,
  useGoogleAuthStatus,
  useLoadDriveFolderChildren,
  useProjectDriveBrowseSource,
  useRemoveDriveRoot,
  useSaveDriveSync,
  useSharedDrives,
  useTriggerDriveSync,
  useUntrackDriveFile,
} from "../api/hooks/useDriveSync";
import { UserContext } from "../auth";

import { DriveAddSelection, DriveAddSourcesDialog } from "./DriveAddSourcesDialog";
import { FolderTree } from "./FolderTree";
import { ProgressBar } from "./ProgressBar";
import { SharedDrivePicker } from "./SharedDrivePicker";
import { describeNewFolders } from "../utils/describeNewFolders";
import { driveOpenUrl } from "../utils/driveOpenUrl";
import { formatDayLong } from "../utils/formatRelative";
import { sameMembers } from "../utils/sameMembers";
import { useConnectGoogle } from "../utils/useConnectGoogle";
import styles from "./DriveSyncSettingsCard.module.css";
import { Chevron } from "./Chevron";

// Where project folders live on the shared drive, so "Add folders or files" opens here.
const PROJECT_FOLDERS_ROOT = "Project Execution";

interface Props {
  projectId: string;
  driveSync: DriveSyncState | null | undefined;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.round(h / 24);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  } catch {
    return iso;
  }
}

function describeAdded(item: { added_by: string; added_at: string | null; reason: string }) {
  const when = formatDayLong(item.added_at);
  const who = item.added_by ? `Added by ${item.added_by}` : "Added";
  return (
    <>
      {when ? `${who}, ${when}` : who}
      {item.reason && <>: &ldquo;{item.reason}&rdquo;</>}
    </>
  );
}

function excludedSetsByRoot(driveSync: DriveSyncState | null | undefined) {
  return Object.fromEntries(
    (driveSync?.roots ?? []).map((r) => [r.folder_id, new Set(r.excluded_subfolder_ids)]),
  );
}

function toggledSet(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export const DriveSyncSettingsCard = ({ projectId, driveSync }: Props) => {
  const { user } = useContext(UserContext);
  const authStatus = useGoogleAuthStatus();
  const { connect: handleConnect, connecting } = useConnectGoogle(
    user?.email ?? undefined,
  );
  const saveDriveSync = useSaveDriveSync();
  const disconnect = useDisconnectDriveSync();
  const takeOver = useUseMyAccountForDriveSync();
  const triggerSync = useTriggerDriveSync();
  const removeRoot = useRemoveDriveRoot();
  const untrackFile = useUntrackDriveFile();
  const addSources = useAddDriveSources();
  const browseSource = useProjectDriveBrowseSource(projectId);
  const actorName =
    [user?.given_name, user?.family_name].filter(Boolean).join(" ") || user?.email || "";

  const sync = useDriveSyncOperation(projectId);
  const syncInFlight = sync.inFlight;
  const trackSync = sync.track;

  const [setupDriveId, setSetupDriveId] = useState<string>("");
  const [excludedByRoot, setExcludedByRoot] = useState<Record<string, Set<string>>>(() =>
    excludedSetsByRoot(driveSync),
  );
  const [excludedFiles, setExcludedFiles] = useState<Set<string>>(
    () => new Set(driveSync?.excluded_drive_file_ids ?? []),
  );
  // Files synced on their own, which is how a file under an excluded folder comes in.
  const [trackedFiles, setTrackedFiles] = useState<Set<string>>(
    () => new Set((driveSync?.files ?? []).map((f) => f.file_id)),
  );
  // Names of what was ticked or unticked, so the save can say what changed.
  const [toggledNames, setToggledNames] = useState<Record<string, string>>({});
  const noteName = (id: string, name: string) =>
    setToggledNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));
  const [excludePatternsText, setExcludePatternsText] = useState<string>(
    driveSync ? driveSync.exclude_name_patterns.join(", ") : "deprecated",
  );
  const [enabled, setEnabled] = useState<boolean>(driveSync?.enabled ?? true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    setExcludePatternsText(
      driveSync ? driveSync.exclude_name_patterns.join(", ") : "deprecated",
    );
    setExcludedByRoot(excludedSetsByRoot(driveSync));
    setExcludedFiles(new Set(driveSync?.excluded_drive_file_ids ?? []));
    setTrackedFiles(new Set((driveSync?.files ?? []).map((f) => f.file_id)));
    setEnabled(driveSync?.enabled ?? true);
  }, [driveSync]);

  const auth = authStatus.data?.authorization ?? null;
  const projectAuthorizer = driveSync?.authorized_user_id;
  const isAuthorizer = !!auth && !!projectAuthorizer && projectAuthorizer === auth.user_id;
  const canSetUp = !!auth && !driveSync;
  const authorizerEmail =
    driveSync?.authorized_user_email || auth?.user_email || "";
  // The BE resolves the authorizer's email at read time; never show the raw kp_ id.
  const authorizerLabel = authorizerEmail || "a teammate";
  const newFoldersText = driveSync
    ? describeNewFolders(driveSync.last_sync_new_folders)
    : null;

  const sharedDrives = useSharedDrives(canSetUp);
  const loadRootChildren = useLoadDriveFolderChildren(projectId, true);
  const driveLabel = driveSync?.drive_name || "Shared drive";

  const patterns = useMemo(
    () =>
      excludePatternsText
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean),
    [excludePatternsText],
  );
  const changedExclusions = useMemo(() => {
    const byRoot: Record<string, string[]> = {};
    for (const root of driveSync?.roots ?? []) {
      const edited = excludedByRoot[root.folder_id];
      if (edited && !sameMembers(Array.from(edited), root.excluded_subfolder_ids)) {
        byRoot[root.folder_id] = Array.from(edited);
      }
    }
    return byRoot;
  }, [driveSync, excludedByRoot]);
  const savedFiles = useMemo(
    () => new Set(driveSync?.excluded_drive_file_ids ?? []),
    [driveSync],
  );
  const excludeFileIds = Array.from(excludedFiles).filter((id) => !savedFiles.has(id));
  const includeFileIds = Array.from(savedFiles).filter((id) => !excludedFiles.has(id));
  const savedTracked = useMemo(
    () => new Set((driveSync?.files ?? []).map((f) => f.file_id)),
    [driveSync],
  );
  const trackFileIds = Array.from(trackedFiles).filter((id) => !savedTracked.has(id));
  const untrackFileIds = Array.from(savedTracked).filter((id) => !trackedFiles.has(id));
  const filesChanged =
    excludeFileIds.length > 0 ||
    includeFileIds.length > 0 ||
    trackFileIds.length > 0 ||
    untrackFileIds.length > 0;
  const filesTabExcluded = useMemo(
    () => new Set(driveSync?.files_tab_excluded_ids ?? []),
    [driveSync],
  );
  // Save starts a sync, so it is offered only when there is something to save.
  const hasChanges =
    !driveSync ||
    enabled !== driveSync.enabled ||
    !sameMembers(patterns, driveSync.exclude_name_patterns) ||
    Object.keys(changedExclusions).length > 0 ||
    filesChanged;

  const startSync = useCallback(async () => {
    try {
      const lro = await triggerSync.mutateAsync({ project_id: projectId });
      trackSync(lro.operation_key);
    } catch (e) {
      setSaveError((e as Error).message || "Starting the Drive sync failed.");
    }
  }, [triggerSync, projectId, trackSync]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setNotice(null);
    if (!driveSync && !setupDriveId) {
      setSaveError("Choose a shared drive before connecting.");
      return;
    }
    try {
      await saveDriveSync.mutateAsync({
        project_id: projectId,
        ...(driveSync ? {} : { drive_id: setupDriveId }),
        exclude_name_patterns: patterns,
        excluded_subfolder_ids_by_root: changedExclusions,
        exclude_file_ids: excludeFileIds,
        include_file_ids: includeFileIds,
        untrack_file_ids: untrackFileIds,
        names: toggledNames,
        enabled,
      });
    } catch (e) {
      console.error("drive-sync save-settings failed", { projectId, error: e });
      setSaveError(
        (e as Error).message ||
          "Saving the Drive sync settings failed. Check the network tab + retry.",
      );
      return;
    }
    if (trackFileIds.length > 0) {
      try {
        await addSources.mutateAsync({
          project_id: projectId,
          folder_ids: [],
          file_ids: trackFileIds,
          actor_name: actorName,
        });
      } catch (e) {
        setSaveError(
          `Saved, but the files ticked under an excluded folder were not added: ${
            (e as Error).message
          }`,
        );
        return;
      }
    }
    // Sync straight away whatever the recurring toggle says: it gates only the schedule.
    await startSync();
  }, [
    driveSync,
    setupDriveId,
    patterns,
    changedExclusions,
    excludeFileIds,
    includeFileIds,
    trackFileIds,
    untrackFileIds,
    addSources,
    actorName,
    toggledNames,
    enabled,
    projectId,
    saveDriveSync,
    startSync,
  ]);

  const handleDisconnect = useCallback(() => {
    if (
      !confirm(
        "Disconnect Drive sync for this project? Files already synced stay, but its folders, files and exclusions are forgotten: set it up again and anything they kept out is ingested. To change whose Google account it syncs through, use \"Sync through my Google account\" instead.",
      )
    )
      return;
    disconnect.mutate({ project_id: projectId });
  }, [disconnect, projectId]);

  const handleUseMyAccount = useCallback(async () => {
    setSaveError(null);
    setNotice(null);
    try {
      await takeOver.mutateAsync({ project_id: projectId });
      setNotice("This Drive sync now runs through your Google account. Nothing else changed.");
    } catch (e) {
      setSaveError((e as Error).message || "Switching the Google account failed.");
    }
  }, [takeOver, projectId]);

  const handleSyncNow = useCallback(async () => {
    setSaveError(null);
    setNotice(null);
    await startSync();
  }, [startSync]);

  const handleRemoveRoot = async (root: DriveSyncRoot) => {
    if (
      !confirm(
        `Remove "${root.folder_name}" from this project's Drive sync?\n\n` +
          "Its files leave the project, apart from any that are also tracked individually.",
      )
    ) {
      return;
    }
    setSaveError(null);
    try {
      await removeRoot.mutateAsync({
        project_id: projectId,
        folder_id: root.folder_id,
        actor_name: actorName,
      });
    } catch (e) {
      setSaveError((e as Error).message || "Removing the folder failed.");
    }
  };

  const handleUntrackFile = async (file: DriveTrackedFile) => {
    if (
      !confirm(
        `Stop syncing "${file.drive_path}"?\n\n` +
          "The file leaves the project. Reinstate it on the Files tab to bring it back.",
      )
    ) {
      return;
    }
    setSaveError(null);
    try {
      await untrackFile.mutateAsync({
        project_id: projectId,
        file_id: file.file_id,
        actor_name: actorName,
      });
    } catch (e) {
      setSaveError((e as Error).message || "Removing the file failed.");
    }
  };

  const handleAdded = (operationKey: string | null) => {
    setAddOpen(false);
    setSaveError(null);
    if (operationKey) {
      setNotice(null);
      sync.track(operationKey);
    } else {
      setNotice("Everything you picked is already synced.");
    }
  };

  const handleAddSources = async ({ folderIds, fileIds }: DriveAddSelection) => {
    const resp = await addSources.mutateAsync({
      project_id: projectId,
      folder_ids: folderIds,
      file_ids: fileIds,
      actor_name: actorName,
    });
    handleAdded(resp.operation_key);
  };

  // -------- Render branches -------- //
  if (authStatus.isLoading) {
    return (
      <section className={styles.card}>
        <h3 className={styles.title}>Connected drive</h3>
        <p className={styles.muted}>Loading authorization status…</p>
      </section>
    );
  }

  // A viewer without Google still sees an existing config, and can manage it.
  if (!auth && !driveSync) {
    return (
      <section className={styles.card}>
        <h3 className={styles.title}>Connected drive</h3>
        <p>
          Connect your Google account once to enable recurring Drive folder
          sync across all your projects.
        </p>
        <p className={styles.muted}>
          Inrange will get read-only access to your Drive (drive.readonly
          scope only) to download files from the folders you register here.
        </p>
        <button
          type="button"
          onClick={handleConnect}
          disabled={connecting}
          className={styles.primaryButton}
        >
          {connecting ? "Redirecting…" : "Connect Drive"}
        </button>
      </section>
    );
  }

  const canEditSettings = canSetUp || !!driveSync;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Connected drive</h3>
        <span className={styles.authorizedAs}>
          {driveSync ? "Synced through" : "Authorized as"} <strong>{authorizerLabel}</strong>
          {isAuthorizer && (
            <>
              {" "}
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                className={styles.linkButton}
              >
                {connecting ? "Redirecting…" : "Reconnect"}
              </button>
            </>
          )}
        </span>
      </header>

      {auth?.status === "failed" && (isAuthorizer || !driveSync) && (
        <div className={styles.warning}>
          Authorization expired - reconnect to keep syncing.
        </div>
      )}

      {driveSync && (
        <p className={styles.driveName}>
          Shared drive: <strong>{driveLabel}</strong>
        </p>
      )}

      {driveSync && !isAuthorizer && (
        <div className={styles.warning}>
          This Drive sync uses <strong>{authorizerLabel}</strong>&apos;s Google account.
          Everyone adds folders and files from the connected shared drive only. You can add
          and remove them, change the exclusions and recurring sync, sync now, or move it onto
          your own Google account, which keeps every folder, file and exclusion.
          {driveSync.last_sync_status === "auth_failed" && (
            <> Its Google connection has expired: move it onto your account to keep syncing.</>
          )}
          {auth?.status !== "failed" && auth ? (
            <>
              {" "}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => void handleUseMyAccount()}
                disabled={takeOver.isLoading}
              >
                {takeOver.isLoading ? "Switching…" : "Sync through my Google account"}
              </button>
            </>
          ) : (
            // Moving it onto your account needs a Google connection of your own first.
            !authStatus.isLoading && (
              <>
                {" "}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? "Redirecting…" : "Connect my Google account"}
                </button>{" "}
                to move it onto yours.
              </>
            )
          )}
        </div>
      )}

      {driveSync ? (
        <>
          <div className={styles.sectionHeader}>
            <span className={styles.label}>Folders ({driveSync.roots.length})</span>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setAddOpen(true)}
            >
              + Add folders or files
            </button>
          </div>
          {driveSync.roots.length === 0 && driveSync.files.length === 0 ? (
            <p className={styles.muted}>
              No folders or files are synced yet. Use &ldquo;+ Add folders or files&rdquo; to
              choose them from {driveLabel}.
            </p>
          ) : driveSync.roots.length === 0 ? (
            <p className={styles.muted}>No folders are synced.</p>
          ) : (
            <ul className={styles.sourceList}>
              {driveSync.roots.map((root) => (
                <DriveSyncRootRow
                  key={root.folder_id}
                  projectId={projectId}
                  root={root}
                  excluded={excludedByRoot[root.folder_id] ?? new Set()}
                  onToggleExcluded={(id, name) => {
                    noteName(id, name);
                    setExcludedByRoot((prev) => ({
                      ...prev,
                      [root.folder_id]: toggledSet(prev[root.folder_id] ?? new Set(), id),
                    }));
                  }}
                  onLoadChildren={loadRootChildren}
                  excludedFiles={excludedFiles}
                  onToggleExcludedFile={(id, name) => {
                    noteName(id, name);
                    setExcludedFiles((prev) => toggledSet(prev, id));
                  }}
                  filesTabExcluded={filesTabExcluded}
                  trackedFiles={trackedFiles}
                  onToggleTrackedFile={(id, name) => {
                    noteName(id, name);
                    setTrackedFiles((prev) => toggledSet(prev, id));
                  }}
                  onRemove={() => void handleRemoveRoot(root)}
                  removing={removeRoot.isLoading}
                  lastSyncOk={driveSync.last_sync_status === "ok"}
                />
              ))}
            </ul>
          )}

          <div className={styles.sectionHeader}>
            <span className={styles.label}>Individual files ({driveSync.files.length})</span>
          </div>
          {driveSync.files.length === 0 ? (
            <p className={styles.muted}>No files are tracked individually.</p>
          ) : (
            <ul className={styles.sourceList}>
              {driveSync.files.map((file) => (
                <li key={file.file_id} className={styles.sourceRow}>
                  <div className={styles.sourceMain}>
                    <span className={styles.sourceName}>{file.drive_path}</span>
                    <span className={styles.sourceMeta}>{describeAdded(file)}</span>
                  </div>
                  <a
                    className={styles.linkButton}
                    href={driveOpenUrl(file.file_id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Drive
                  </a>
                  <button
                    type="button"
                    className={styles.dangerButton}
                    onClick={() => void handleUntrackFile(file)}
                    disabled={untrackFile.isLoading}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="drive-sync-shared-drive">
            Shared drive
          </label>
          <SharedDrivePicker
            query={sharedDrives}
            id="drive-sync-shared-drive"
            value={setupDriveId}
            onChange={setSetupDriveId}
            placeholder="Choose a shared drive"
            emptyMessageSubject="this project's"
          />
          {(sharedDrives.data?.drives.length ?? 0) > 0 && (
            <p className={styles.helpText}>
              Once connected, add folders and files from this shared drive with
              &ldquo;+ Add folders or files&rdquo;.
            </p>
          )}
        </div>
      )}

      <div className={styles.field}>
        <label className={styles.label}>Name exclusion patterns</label>
        <input
          type="text"
          className={styles.input}
          value={excludePatternsText}
          onChange={(e) => setExcludePatternsText(e.target.value)}
          placeholder="deprecated, archive"
          disabled={!canEditSettings}
        />
        <p className={styles.helpText}>
          Comma-separated. Files (or folders on the path) whose name contains any of these
          are skipped, under every synced folder. Case-insensitive substring match. They
          never apply to an individually tracked file.
        </p>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!canEditSettings}
          />
          Enable recurring sync (every 15 min, 24/7)
        </label>
      </div>

      <div className={styles.statusLine}>
        {driveSync ? (
          <>
            Last sync: <strong>{relativeTime(driveSync.last_synced_at)}</strong>
            {driveSync.last_sync_status && (
              <> · status: <strong>{driveSync.last_sync_status}</strong></>
            )}
            {(driveSync.last_sync_added_count > 0 ||
              driveSync.last_sync_changed_count > 0 ||
              driveSync.last_sync_removed_count > 0) && (
              <>
                {" · "}
                {driveSync.last_sync_added_count} added,{" "}
                {driveSync.last_sync_changed_count} updated,{" "}
                {driveSync.last_sync_removed_count} removed
              </>
            )}
            {newFoldersText && (
              <>
                {" · "}
                {newFoldersText}
              </>
            )}
            {driveSync.last_sync_error && (
              <p className={styles.error}>Error: {driveSync.last_sync_error}</p>
            )}
          </>
        ) : (
          <span className={styles.muted}>Not yet synced.</span>
        )}
      </div>

      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}
      {sync.error && (
        <p className={styles.error} role="alert">
          Sync failed: {sync.error}
        </p>
      )}
      {notice && <p className={styles.helpText}>{notice}</p>}
      <div className={styles.actions}>
        <button
          type="button"
          onClick={handleSave}
          disabled={
            !canEditSettings ||
            !hasChanges ||
            (!driveSync && !setupDriveId) ||
            saveDriveSync.isLoading
          }
          className={styles.primaryButton}
        >
          {saveDriveSync.isLoading
            ? driveSync
              ? "Saving…"
              : "Connecting…"
            : driveSync
              ? "Save"
              : "Connect shared drive"}
        </button>
        {driveSync && (
          <>
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={syncInFlight || triggerSync.isLoading}
              className={styles.secondaryButton}
              title={
                enabled
                  ? "Pull the latest files from Drive right now"
                  : "Auto-sync is paused. Click to do a one-off manual pull."
              }
            >
              {syncInFlight || triggerSync.isLoading ? "Syncing…" : "Sync now"}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnect.isLoading}
              className={styles.dangerButton}
            >
              Disconnect
            </button>
          </>
        )}
      </div>

      {sync.progress && (
        <div className={styles.syncResult}>
          <ProgressBar progress={sync.progress} />
        </div>
      )}

      {driveSync && (
        <DriveAddSourcesDialog
          open={addOpen}
          driveName={driveSync.drive_name}
          destination="the project"
          source={browseSource}
          adding={addSources.isLoading}
          startInFolder={PROJECT_FOLDERS_ROOT}
          onAdd={handleAddSources}
          onClose={() => setAddOpen(false)}
        />
      )}
    </section>
  );
};

const EXCLUSION_HELP =
  "Untick a subfolder to exclude it and everything under it. To exclude single files, " +
  "click a folder's file count. Under an excluded folder, tick a file to sync it on its own.";

interface RootRowProps {
  projectId: string;
  root: DriveSyncRoot;
  excluded: Set<string>;
  onToggleExcluded: (folderId: string, name: string) => void;
  onLoadChildren: (folderId: string) => Promise<DriveFolderNode>;
  excludedFiles: Set<string>;
  onToggleExcludedFile: (fileId: string, name: string) => void;
  filesTabExcluded: Set<string>;
  trackedFiles: Set<string>;
  onToggleTrackedFile: (fileId: string, name: string) => void;
  onRemove: () => void;
  removing: boolean;
  lastSyncOk: boolean;
}

// Its own component so each root lazily loads its folder tree only once expanded.
const DriveSyncRootRow = ({
  projectId,
  root,
  excluded,
  onToggleExcluded,
  onLoadChildren,
  excludedFiles,
  onToggleExcludedFile,
  filesTabExcluded,
  trackedFiles,
  onToggleTrackedFile,
  onRemove,
  removing,
  lastSyncOk,
}: RootRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const tree = useDriveFolderTree(root.folder_id, expanded, projectId, true);
  const excludedCount = root.excluded_subfolder_ids.length;

  return (
    <li className={styles.sourceRowBlock}>
      <div className={styles.sourceRow}>
        <button
          type="button"
          className={styles.chevron}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide folder structure" : "Show folder structure"}
        >
          <Chevron tree open={expanded} />
        </button>
        <div className={styles.sourceMain}>
          <span className={styles.sourceName}>
            {root.folder_name || root.folder_id}
            {excludedCount > 0 && (
              <span className={styles.sourceMeta}>
                {" "}
                · {excludedCount} subfolder{excludedCount === 1 ? "" : "s"} excluded
              </span>
            )}
          </span>
          <span className={styles.sourceMeta}>{describeAdded(root)}</span>
        </div>
        <a
          className={styles.linkButton}
          href={driveOpenUrl(root.folder_id)}
          target="_blank"
          rel="noreferrer"
        >
          Open in Drive
        </a>
        <button
          type="button"
          className={styles.dangerButton}
          onClick={onRemove}
          disabled={removing}
        >
          Remove
        </button>
      </div>
      {expanded && (
        <div className={styles.sourceTree}>
          {tree.isLoading && <p className={styles.muted}>Loading folder tree from Drive…</p>}
          {tree.isError && (
            <p className={styles.error}>
              Couldn&apos;t load the folder preview.{" "}
              {lastSyncOk
                ? "The sync itself is working, so the folder is fine - this is just the preview. "
                : ""}
              <button
                type="button"
                onClick={() => void tree.refetch()}
                className={styles.linkButton}
              >
                Retry
              </button>
            </p>
          )}
          {tree.data && (
            <>
              <p className={styles.helpText}>{EXCLUSION_HELP} Save to apply.</p>
              <FolderTree
                tree={tree.data.tree}
                excludedSubfolderIds={excluded}
                onToggleExcluded={onToggleExcluded}
                onLoadChildren={onLoadChildren}
                excludedFileIds={excludedFiles}
                onToggleExcludedFile={onToggleExcludedFile}
                lockedFileIds={filesTabExcluded}
                trackedFileIds={trackedFiles}
                onToggleTrackedFile={onToggleTrackedFile}
              />
            </>
          )}
        </div>
      )}
    </li>
  );
};
